import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getPublicVoiceSetting, VoiceSetting } from '../../api/payment/donate-voice.api';
import { MAX_VOICE_MS, formatVoiceSeconds, voiceEligibility } from '../../utils/voice-message';
import { useVoiceRecorder } from '../../hooks/use-voice-recorder';

// Khối ghi âm lời nhắn giọng nói trong khung donate (trang cá nhân + bài viết).
// Chỉ hiện khi streamer đã bật tính năng và số Ca donate đủ mức tối thiểu họ đặt.

export interface VoiceRecorderValue {
  blob: Blob;
  mime: string;
}

interface VoiceRecorderProps {
  creatorId: string;
  amount: number;
  onChange: (v: VoiceRecorderValue | null) => void;
  /** Khung donate dùng để khoá nút Donate trong lúc đang ghi âm (tránh gửi dở chừng). */
  onRecordingChange?: (recording: boolean) => void;
}

const VoiceRecorder: React.FC<VoiceRecorderProps> = ({
  creatorId,
  amount,
  onChange,
  onRecordingChange,
}) => {
  const [setting, setSetting] = useState<VoiceSetting | null>(null);
  const { state, elapsedMs, url, blob, mime, start, stop, reset } = useVoiceRecorder();

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onRecordingChangeRef = useRef(onRecordingChange);
  onRecordingChangeRef.current = onRecordingChange;

  useEffect(() => {
    onRecordingChangeRef.current?.(state === 'recording');
  }, [state]);

  useEffect(() => {
    let cancelled = false;
    setSetting(null);
    if (!creatorId) return undefined;
    getPublicVoiceSetting(creatorId)
      .then((s) => {
        if (!cancelled) setSetting(s);
      })
      .catch(() => {
        if (!cancelled) setSetting({ enabled: false, minCa: 0 });
      });
    return () => {
      cancelled = true;
    };
  }, [creatorId]);

  const eligibility = useMemo(() => voiceEligibility(setting, amount), [setting, amount]);

  // Báo cho khung donate biết bản ghi có được gửi kèm hay không — giữ nguyên bản ghi khi
  // tạm thời không đủ Ca, chỉ ẩn khỏi lượt gửi (quay lại đủ Ca thì onChange lại được gọi).
  useEffect(() => {
    if (eligibility === 'ok' && blob && mime) {
      onChangeRef.current({ blob, mime });
    } else {
      onChangeRef.current(null);
    }
  }, [eligibility, blob, mime]);

  // Dọn bản ghi khi đổi người nhận (ghi âm cũ không còn thuộc về donate mới).
  useEffect(() => {
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creatorId]);

  if (eligibility === 'off') return null;

  if (eligibility === 'below-min') {
    return (
      <p className="text-xs ui-muted mt-1">
        Donate từ {(setting?.minCa ?? 0).toLocaleString('vi-VN')} Ca để gửi kèm lời nhắn giọng nói
      </p>
    );
  }

  const clock = `${formatVoiceSeconds(elapsedMs)} / ${formatVoiceSeconds(MAX_VOICE_MS)}`;

  return (
    <div className="flex flex-col gap-2 mt-2 ui-surface-2 rounded-lg p-3">
      <span className="text-xs font-medium ui-muted">Lời nhắn giọng nói (tối đa 15 giây)</span>

      {state === 'unsupported' && (
        <p className="text-xs ui-muted">Trình duyệt này chưa hỗ trợ ghi âm.</p>
      )}

      {state === 'denied' && (
        <p className="text-xs ui-muted">
          Bạn đã chặn micro. Mở quyền micro cho cab.vn trong cài đặt trình duyệt rồi thử lại.
        </p>
      )}

      {state === 'idle' && (
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => start()}
            className="inline-flex items-center justify-center rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium px-3 py-1.5"
          >
            Ghi âm
          </button>
          <span className="text-xs ui-muted tabular-nums">{clock}</span>
        </div>
      )}

      {state === 'recording' && (
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => stop()}
            className="inline-flex items-center justify-center rounded-md bg-red-600 hover:bg-red-700 text-white text-xs font-medium px-3 py-1.5"
          >
            Dừng
          </button>
          <span className="text-xs ui-muted tabular-nums">{clock}</span>
        </div>
      )}

      {state === 'recorded' && (
        <div className="flex flex-col gap-2">
          {url && (
            <audio controls src={url} className="h-9 w-full max-w-xs">
              <track kind="captions" />
            </audio>
          )}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => start()}
              className="text-xs font-medium text-primary hover:underline"
            >
              Ghi lại
            </button>
            <button
              type="button"
              onClick={() => reset()}
              className="text-xs font-medium text-red-600 hover:underline"
            >
              Xoá
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceRecorder;
